import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";

const DEMO_TEXT = `Il pomeriggio trascorreva lento tra i vicoli del centro. Un uomo anziano leggeva il giornale seduto su una panchina di pietra, mentre i piccioni beccavano briciole vicino ai suoi piedi. Il profumo del caffè appena fatto usciva dal bar all'angolo e si mescolava con l'aria tiepida di settembre.`;

interface WordToken {
  wordBegin: number;
  wordEnd: number;
  sentenceBegin: number;
  sentenceEnd: number;
  word: string;
}

function tokenizeWords(text: string): WordToken[] {
  const tokens: WordToken[] = [];
  let i = 0;
  while (i < text.length) {
    if (/[\s.,!?;:"'\[\]{}()]/.test(text[i])) { i++; continue; }
    let end = i;
    while (end < text.length && !/[\s.,!?;:"'\[\]{}()]/.test(text[end])) end++;
    tokens.push({ wordBegin: i, wordEnd: end, sentenceBegin: 0, sentenceEnd: text.length, word: text.slice(i, end) });
    i = end;
  }
  return tokens;
}

function splitIntoSentences(text: string): { sentenceBegin: number; sentenceEnd: number }[] {
  const sentences: { sentenceBegin: number; sentenceEnd: number }[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "." || text[i] === "!" || text[i] === "?") {
      sentences.push({ sentenceBegin: start, sentenceEnd: i + 1 });
      start = i + 1;
      while (start < text.length && text[start] === " ") start++;
    }
  }
  if (start < text.length) sentences.push({ sentenceBegin: start, sentenceEnd: text.length });
  return sentences;
}

function canJoin(left: WordToken, right: WordToken) {
  return left.sentenceBegin === right.sentenceBegin;
}

export const Route = createFileRoute("/app/vocabulary_composer_mockup")({
  component: MockupPage,
});

function MockupPage() {
  const [savedStarts, setSavedStarts] = useState<number[]>([]);
  const [savedGroups, setSavedGroups] = useState<number[][]>([]);

  const allTokens = useMemo(() => tokenizeWords(DEMO_TEXT), []);
  const sentences = useMemo(() => splitIntoSentences(DEMO_TEXT), []);
  const tokensWithSentence = useMemo(() => allTokens.map(t => {
    const s = sentences.find(sen => sen.sentenceBegin <= t.wordBegin && t.wordEnd <= sen.sentenceEnd);
    return { ...t, sentenceBegin: s?.sentenceBegin ?? 0, sentenceEnd: s?.sentenceEnd ?? DEMO_TEXT.length };
  }), [allTokens, sentences]);

  const savedSet = useMemo(() => new Set(savedStarts), [savedStarts]);

  const groupIdxByStart = useMemo(() => {
    const m = new Map<number, number>();
    savedGroups.forEach((group, idx) => {
      for (const start of group) m.set(start, idx);
    });
    return m;
  }, [savedGroups]);

  const handleTap = (t: WordToken) => {
    if (!savedSet.has(t.wordBegin)) {
      setSavedStarts(prev => [...prev, t.wordBegin]);
    }
  };

  const handleJoin = (left: WordToken, right: WordToken) => {
    setSavedGroups(prev => {
      const leftIdx = prev.findIndex(g => g.includes(left.wordBegin));
      const rightIdx = prev.findIndex(g => g.includes(right.wordBegin));

      if (leftIdx >= 0 && leftIdx === rightIdx) {
        const group = prev[leftIdx];
        setSavedStarts(s => Array.from(new Set([...s, ...group])).sort((a, b) => a - b));
        return prev.filter((_, i) => i !== leftIdx);
      }

      const remaining = prev.filter((_, i) => i !== leftIdx && i !== rightIdx);

      if (leftIdx >= 0 && rightIdx >= 0) {
        const merged = [...new Set([...prev[leftIdx], ...prev[rightIdx]])].sort((a, b) => a - b);
        return [...remaining, merged];
      }

      if (leftIdx >= 0) {
        setSavedStarts(s => s.filter(wb => wb !== right.wordBegin));
        const updated = [...prev[leftIdx], right.wordBegin].sort((a, b) => a - b);
        return [...remaining, updated];
      }

      if (rightIdx >= 0) {
        setSavedStarts(s => s.filter(wb => wb !== left.wordBegin));
        const updated = [...prev[rightIdx], left.wordBegin].sort((a, b) => a - b);
        return [...remaining, updated];
      }

      setSavedStarts(s => s.filter(wb => wb !== left.wordBegin && wb !== right.wordBegin));
      return [...remaining, [left.wordBegin, right.wordBegin].sort((a, b) => a - b)];
    });
  };

  const joinPairs: { left: WordToken; right: WordToken }[] = [];
  for (let i = 0; i < tokensWithSentence.length - 1; i++) {
    const a = tokensWithSentence[i];
    const b = tokensWithSentence[i + 1];
    const inSameGroup =
      groupIdxByStart.has(a.wordBegin) &&
      groupIdxByStart.has(b.wordBegin) &&
      groupIdxByStart.get(a.wordBegin) === groupIdxByStart.get(b.wordBegin);
    const aSaved = savedSet.has(a.wordBegin) || groupIdxByStart.has(a.wordBegin);
    const bSaved = savedSet.has(b.wordBegin) || groupIdxByStart.has(b.wordBegin);
    if (aSaved && bSaved && !inSameGroup && canJoin(a, b)) {
      joinPairs.push({ left: a, right: b });
    }
  }

  const renderText = () => {
    const spans: React.ReactNode[] = [];
    let last = 0;
    let i = 0;

    const savedBg = "bg-[linear-gradient(180deg,rgba(218,208,234,0.50)_0%,rgba(189,177,219,0.49)_52%,rgba(233,226,242,0.48)_100%)]";
    const baseCls = "border-0 p-0 font-inherit text-inherit align-baseline cursor-pointer appearance-none bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500/80 focus-visible:ring-offset-1";

    const renderGap = (from: number, to: number, joinPair: { left: WordToken; right: WordToken } | undefined) => {
      if (from >= to) return;
      if (joinPair) {
        spans.push(
          <span
            key={`bridge-${from}`}
            className="inline-flex h-[16px] w-[10px] cursor-pointer items-center justify-center align-middle"
            onClick={() => handleJoin(joinPair.left, joinPair.right)}
          >
            <span className="inline-block h-[5px] w-[5px] rounded-full bg-purple-400/60 transition-colors" />
          </span>
        );
      } else {
        spans.push(<span key={`g-${from}`}>{DEMO_TEXT.slice(from, to)}</span>);
      }
    };

    while (i < tokensWithSentence.length) {
      const t = tokensWithSentence[i];
      const gIdx = groupIdxByStart.get(t.wordBegin);

      if (gIdx !== undefined) {
        const group: WordToken[] = [];
        let j = i;
        while (j < tokensWithSentence.length && groupIdxByStart.get(tokensWithSentence[j].wordBegin) === gIdx) {
          group.push(tokensWithSentence[j]);
          j++;
        }

        const first = group[0];
        const rightPairG = joinPairs.find(p => p.right.wordBegin === first.wordBegin);
        renderGap(last, first.wordBegin, rightPairG);

        const phraseChildren: React.ReactNode[] = [];
        let pLast = first.wordBegin;

        for (let k = 0; k < group.length; k++) {
          const gt = group[k];
          if (gt.wordBegin > pLast) {
            phraseChildren.push(<span key={`pg-${gt.wordBegin}`}>{DEMO_TEXT.slice(pLast, gt.wordBegin)}</span>);
          }

          let btnCls = baseCls;

          phraseChildren.push(
            <button key={`pw-${gt.wordBegin}`} type="button"
              className={btnCls}
              onClick={(e) => { e.stopPropagation(); handleTap(gt); }} aria-label={gt.word}>
              {gt.word}
            </button>
          );

          pLast = gt.wordEnd;
        }

        spans.push(
          <span key={`phrase-${gIdx}`}
            className={`${savedBg} inline-block align-baseline cursor-pointer`}
            onClick={() => {
              setSavedGroups(prev => {
                const grp = prev[gIdx];
                if (grp) {
                  setSavedStarts(s => Array.from(new Set([...s, ...grp])).sort((a, b) => a - b));
                }
                return prev.filter((_, ii) => ii !== gIdx);
              });
            }}
          >
            {phraseChildren}
          </span>
        );

        last = group[group.length - 1].wordEnd;
        i = j;
      } else {
        const rightPairS = joinPairs.find(p => p.right.wordBegin === t.wordBegin);
        renderGap(last, t.wordBegin, rightPairS);

        const isSaved = savedSet.has(t.wordBegin);
        let cls = baseCls + " transition-[color,opacity] duration-200";
        if (isSaved) cls += ` ${savedBg}`;

        spans.push(
          <button key={`w-${t.wordBegin}`} type="button"
            className={cls}
            onClick={() => handleTap(t)} aria-label={t.word}>
            {t.word}
          </button>
        );

        last = t.wordEnd;
        i++;
      }
    }
    if (last < DEMO_TEXT.length) spans.push(<span key="g-end">{DEMO_TEXT.slice(last)}</span>);
    return spans;
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
      <div className="rounded-[26px] border border-slate-200 bg-white px-6 py-8">
        <div className="font-serif text-lg leading-relaxed text-slate-900">{renderText()}</div>
      </div>
    </div>
  );
}
